/** Formateo de métricas.
 *
 * Todas las tasas viajan como fracción desde el backend (0.42 = 42 %), y el
 * drawdown viaja negativo. Convertir a porcentaje es cosa de la vista: en la
 * base y en los cálculos conviene la fracción.
 */

const EMPTY = '—'

/** Los campos `Decimal` del backend viajan como string (Pydantic los serializa
 *  así para no perder precisión) — `formatPercent`/`formatNumber` aceptan ese
 *  string directo en vez de forzar a cada caller a acordarse de `Number(...)`
 *  antes de llamarlos. Sin esto, olvidarse la conversión deja pasar el string
 *  crudo del Decimal a la vista tal cual — con sus 18 decimales de por medio,
 *  ya pasó más de una vez en la tabla de perfiles de riesgo y la card del bot. */
type Numeric = number | string | null | undefined

export function toNumber(value: Numeric): number | null {
  if (value === null || value === undefined) return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function formatPercent(value: Numeric, digits = 1): string {
  const parsed = toNumber(value)
  if (parsed === null) return EMPTY
  // Espacio irrompible: en una columna angosta (la tarjeta de Robustez con
  // las etiquetas largas del Monte Carlo, por ejemplo) un espacio normal
  // dejaba "100.0" y "%" en líneas separadas.
  return `${(parsed * 100).toFixed(digits)} %`
}

export function formatNumber(value: Numeric, digits = 2): string {
  const parsed = toNumber(value)
  if (parsed === null) return EMPTY
  return parsed.toFixed(digits)
}

/** Multiplicadores (apalancamiento): números casi siempre "redondos" (1×, 2×,
 *  5×) que un `.toFixed(2)` a secas deja con ceros de relleno inútiles
 *  ("1.00×"), pero sin perder la fracción real cuando la hay ("1.5×"). */
export function formatMultiplier(value: Numeric): string {
  const parsed = toNumber(value)
  if (parsed === null) return EMPTY
  return parsed.toFixed(2).replace(/\.?0+$/, '')
}

/** Para el VALOR de un input editable de monto (no para mostrar en texto):
 *  recorta a una precisión razonable pero sin ceros de relleno, así "29.87"
 *  del backend no aparece como "29.870000000000000000" (Decimal de 18
 *  decimales serializado a string) ni una suma con el saldo de la cuenta
 *  como "10.31781681" cuando alcanza con menos cifras. */
export function formatAmountInput(value: Numeric, maxDecimals = 8): string {
  const parsed = toNumber(value)
  if (parsed === null) return ''
  return parsed.toFixed(maxDecimals).replace(/\.?0+$/, '')
}

/** Todo el dinero de la app es USDT (Fase 2 del plan es paper trading contra
 *  ese par). Pasó por dos formatos antes ("12.40 USDT", después "$12.40")
 *  — a pedido del usuario, ninguno de los dos hace falta: en el contexto
 *  donde aparece (P&L, equity, comisiones) ya se entiende que es plata,
 *  un número solo alcanza.
 *
 *  Decimales "inteligentes" por magnitud, no siempre dos fijos: 2
 *  centavos sobre 10615 no dicen nada útil, sobre 0.33 son la mitad del
 *  valor — a más dígitos enteros, menos decimales hacen falta para leerlo
 *  con precisión que importe. */
export function formatUsdt(value: Numeric): string {
  const parsed = toNumber(value)
  if (parsed === null) return EMPTY
  const abs = Math.abs(parsed)
  const digits = abs >= 1000 ? 0 : abs >= 100 ? 1 : 2
  return parsed.toFixed(digits)
}

/** Plata en un espacio chico (una celda de calendario/heatmap): con signo
 *  explícito y abreviado en miles a partir de $10.000 — `formatUsdt` ahí
 *  desbordaría ("93452878") o perdería el signo de una pérdida sin tono de
 *  color al lado (una celda de calendario no siempre tiene uno). */
export function formatCompactUsdt(value: Numeric): string {
  const parsed = toNumber(value)
  if (parsed === null) return EMPTY
  const sign = parsed > 0 ? '+' : parsed < 0 ? '−' : ''
  const magnitude = Math.abs(parsed)
  if (magnitude >= 100_000) return `${sign}$${(magnitude / 1000).toFixed(0)}k`
  if (magnitude >= 10_000) return `${sign}$${(magnitude / 1000).toFixed(1)}k`
  // Por debajo de $1 el redondeo a entero pierde el valor por completo
  // ($0.03 y $0.70 mostrarían los dos "$0") — en una flota paper con
  // montos chicos eso se ve como varias barras/celdas de distinto
  // tamaño y color que dicen todas "$0".
  if (magnitude > 0 && magnitude < 1) return `${sign}$${magnitude.toFixed(2)}`
  return `${sign}$${Math.round(magnitude).toLocaleString('es-AR')}`
}

/** Precios y cantidades chicas: DOGE cotiza a 0.07, una posición en BTC es
 *  0.00038 — con dos decimales fijos se ven "0.00". Por debajo de 1, cuatro
 *  cifras significativas en vez de un punto fijo de decimales. */
export function formatPrice(value: Numeric): string {
  const parsed = toNumber(value)
  if (parsed === null) return EMPTY
  if (parsed !== 0 && Math.abs(parsed) < 1) return parsed.toPrecision(4)
  return parsed.toFixed(2)
}

export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY
  return value.toLocaleString('es-AR')
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY
  return value.slice(0, 10)
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EMPTY
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
}

/** Cuánto hace que arrancó, en la unidad más grande que tenga sentido. */
export function formatDuration(startedAt: string | null | undefined): string {
  if (!startedAt) return EMPTY
  const start = new Date(startedAt)
  if (Number.isNaN(start.getTime())) return EMPTY
  const ms = Math.max(0, Date.now() - start.getTime())
  const days = Math.floor(ms / 86_400_000)
  const hours = Math.floor((ms % 86_400_000) / 3_600_000)
  if (days > 0) return `${days} d ${hours} h`
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`
}

/** "hace 18 min" / "hace 2 h" / "hace 3 d" — la unidad más grande que tenga
 *  sentido, sin componer dos como `formatDuration` (que sí da "1 d 4 h"):
 *  para el timestamp de una alerta alcanza con una aproximación, no con
 *  precisión al minuto. */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return EMPTY
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY
  const ms = Math.max(0, Date.now() - date.getTime())
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'recién'
  if (minutes < 60) return `hace ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `hace ${hours} h`
  const days = Math.floor(hours / 24)
  return `hace ${days} d`
}

/** Duración en barras a algo legible: 4320 barras en 1h son medio año. */
export function formatBars(bars: number | null | undefined, timeframe: string): string {
  if (bars === null || bars === undefined) return EMPTY
  const hoursPerBar: Record<string, number> = {
    '5m': 5 / 60,
    '15m': 0.25,
    '30m': 0.5,
    '1h': 1,
    '4h': 4,
    '1d': 24,
  }
  const hours = (hoursPerBar[timeframe] ?? 1) * bars
  const days = hours / 24
  if (days >= 365) return `${formatInt(bars)} barras (${(days / 365).toFixed(1)} años)`
  if (days >= 1) return `${formatInt(bars)} barras (${Math.round(days)} días)`
  return `${formatInt(bars)} barras`
}

/** Clase CSS según el signo, para no leer un CAGR negativo como si fuera bueno. */
export function toneClass(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return ''
  if (value > 0) return 'tone-positive'
  if (value < 0) return 'tone-negative'
  return ''
}
